import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
} from 'react';
import styles from './MoneyInput.module.css';

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  /** Current committed value; use null for "not entered yet" (actuals). */
  value: number | null;
  onCommit(next: number | null): void;
  debounceMs?: number;
  /** When true, use text-sized styling instead of tiny cell size. */
  text?: boolean;
  className?: string;
}

const DIGITS = /[^0-9]/g;

function fromInput(raw: string): number | null {
  const cleaned = raw.replace(DIGITS, '');
  if (cleaned === '') return null;
  return parseInt(cleaned, 10);
}

export function MoneyInput({
  value,
  onCommit,
  debounceMs = 300,
  text = false,
  className = '',
  onFocus,
  ...rest
}: Props) {
  const [draft, setDraft] = useState<string>(value === null ? '' : String(value));
  const commitTimer = useRef<number | null>(null);
  const lastCommitted = useRef<number | null>(value);

  // Sync external value → draft when external changes and user isn't editing
  useEffect(() => {
    // If external value matches what we last committed, don't clobber the draft
    if (value === lastCommitted.current) return;
    setDraft(value === null ? '' : String(value));
    lastCommitted.current = value;
  }, [value]);

  function scheduleCommit(next: number | null) {
    if (commitTimer.current !== null) window.clearTimeout(commitTimer.current);
    commitTimer.current = window.setTimeout(() => {
      lastCommitted.current = next;
      onCommit(next);
    }, debounceMs);
  }

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    const cleaned = e.target.value.replace(DIGITS, '');
    setDraft(cleaned);
    scheduleCommit(fromInput(cleaned));
  }

  function handleFocus(e: FocusEvent<HTMLInputElement>) {
    e.target.select();
    onFocus?.(e);
  }

  const cls = text ? `${styles.input} ${styles.textish}` : styles.input;
  return (
    <input
      {...rest}
      type="text"
      inputMode="numeric"
      className={`${cls} ${className}`}
      value={draft}
      onChange={onChange}
      onFocus={handleFocus}
    />
  );
}
